// components/Medicines/MedicineDetailsModal.js
import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Badge } from "reactstrap";

const MedicineDetailsModal = ({ isOpen, toggle, medicine }) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!medicine) return null;

  const formatCurrency = (amount) => {
    return `৳${parseFloat(amount || 0).toLocaleString('en-BD', { minimumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status) => {
    return status ? (
      <Badge color="success">
        <i className="fas fa-check me-1"></i> Available
      </Badge>
    ) : (
      <Badge color="danger">
        <i className="fas fa-times me-1"></i> Unavailable
      </Badge>
    );
  };

  const getClearanceBadge = (clearance) => {
    return clearance ? (
      <Badge color="warning">
        <i className="fas fa-tag me-1"></i> Clearance
      </Badge>
    ) : null;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Medicine Details - {medicine.productname || medicine.medicinename}
        <div className="mt-1">
          <small className="text-muted">ID: #{medicine.id}</small>
        </div>
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading medicine details...</p>
          </div>
        ) : (
          <div className="row">
            {/* Left Column - Image and Basic Info */}
            <div className="col-md-5">
              <div className="text-center mb-4">
                <img
                  src={medicine.imagelink || "https://static.vecteezy.com/system/resources/thumbnails/002/272/156/small_2x/medicine-drug-icon-flat-style-and-colorful-design-illustration-free-vector.jpg"}
                  alt={medicine.productname || medicine.medicinename}
                  className="img-fluid rounded border"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.src = "https://static.vecteezy.com/system/resources/thumbnails/002/272/156/small_2x/medicine-drug-icon-flat-style-and-colorful-design-illustration-free-vector.jpg";
                  }}
                />
              </div>

              {/* <div className="d-flex justify-content-center gap-2 mb-3">
                {getStatusBadge(medicine.availablestatus)}
                {getClearanceBadge(medicine.clearance)}
              </div> */}

              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">Quick Info</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td className="text-muted">MRP:</td>
                        <td className="fw-bold text-primary">{formatCurrency(medicine.mrp || medicine.regularunitprice)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Size:</td>
                        <td>{medicine.size || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Packaging:</td>
                        <td>{medicine.packaging || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Quantity:</td>
                        <td>{medicine.quantity || "1"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Origin:</td>
                        <td>{medicine.origin || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Rating:</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="fas fa-star text-warning me-1"></i>
                            <span>{medicine.rating || "0"}/5</span>
                            {medicine.avgrating && (
                              <small className="text-muted ms-2">
                                (Avg: {medicine.avgrating})
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>

            {/* Right Column - Detailed Information */}
            <div className="col-md-7">
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title">Product Information</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td className="text-muted" width="40%">Product Name:</td>
                        <td className="fw-semibold">{medicine.productname || medicine.medicinename}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Brand:</td>
                        <td>{medicine.productbrand || medicine.companyname || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Vendor:</td>
                        <td>{medicine.vandor || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Category:</td>
                        <td>
                          <Badge color="info" className="me-1">{medicine.category || "N/A"}</Badge>
                          {medicine.subcategory && (
                            <Badge color="secondary">{medicine.subcategory}</Badge>
                          )}
                        </td>
                      </tr>
                      {medicine.genericname && (
                        <tr>
                          <td className="text-muted">Generic Name:</td>
                          <td>{medicine.genericname}</td>
                        </tr>
                      )}
                      {medicine.types && (
                        <tr>
                          <td className="text-muted">Type:</td>
                          <td>{medicine.types}</td>
                        </tr>
                      )}
                      {medicine.power && (
                        <tr>
                          <td className="text-muted">Power/Strength:</td>
                          <td>{medicine.power}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Product Details */}
              {medicine.details && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title">Product Details</h6>
                    <div className="product-details" style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontSize: '0.9rem',
                      lineHeight: '1.5'
                    }}>
                      {medicine.details.split('\n').map((paragraph, index) => (
                        <p key={index} className={index > 0 ? 'mt-2' : ''}>
                          {paragraph.trim()}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Images */}
              {(medicine.image1 || medicine.image2 || medicine.image3) && (
                <div className="card">
                  <div className="card-body">
                    <h6 className="card-title">Additional Images</h6>
                    <div className="d-flex gap-2">
                      {[medicine.image1, medicine.image2, medicine.image3].map((img, index) => (
                        img && (
                          <img
                            key={index}
                            src={img}
                            alt={`Additional ${index + 1}`}
                            className="img-thumbnail"
                            style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                          />
                        )
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>

      </ModalFooter>
    </Modal>
  );
};

export default MedicineDetailsModal;